import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { TreatmentPicker } from "@/components/treatment-picker";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, X } from "lucide-react";

const schema = z.object({
  title: z.string().min(1, "Tytuł jest wymagany"),
  subtitle: z.string().optional(),
  sortOrder: z.coerce.number().int(),
  isActive: z.boolean(),
  validFrom: z.string().optional(),
  validUntil: z.string().optional(),
  /** Ordered list of treatment ids — order of array = display order in the rail.
   *  A rail needs at least one; the inspirations list may stay empty. */
  treatmentIds: z.array(z.string().uuid()),
});

type FormData = z.infer<typeof schema>;

export type CollectionForEdit = {
  id: string;
  title: string;
  subtitle: string | null;
  sortOrder: number;
  isActive: boolean;
  validFrom: Date | string | null;
  validUntil: Date | string | null;
  treatments: { id: string; name: string }[];
};

/** The one list under the rails on the app's home screen ("Inspiracje"). */
export type InspirationsForEdit = {
  title: string;
  subtitle: string | null;
  isActive: boolean;
  treatments: { id: string; name: string }[];
};

export function CollectionDialog({
  open,
  onOpenChange,
  collection,
  inspirations,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collection?: CollectionForEdit | null;
  /** Edits the inspirations list instead of a rail. */
  inspirations?: InspirationsForEdit | null;
}) {
  const utils = trpc.useUtils();
  const isInspirations = !!inspirations;
  const isEdit = !!collection;

  const { data: treatmentList } = trpc.treatment.list.useQuery(
    { page: 1, limit: 100 },
    { enabled: open },
  );

  const createMutation = trpc.collection.create.useMutation({
    onSuccess: () => {
      utils.collection.list.invalidate();
      toast.success("Kolekcja dodana");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.collection.update.useMutation({
    onSuccess: () => {
      utils.collection.list.invalidate();
      toast.success("Kolekcja zaktualizowana");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const updateInspirations = trpc.collection.updateInspirations.useMutation({
    onSuccess: () => {
      utils.collection.getInspirations.invalidate();
      toast.success("Inspiracje zaktualizowane");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      subtitle: "",
      sortOrder: 0,
      isActive: true,
      validFrom: "",
      validUntil: "",
      treatmentIds: [],
    },
  });

  useEffect(() => {
    if (!open) return;
    if (inspirations) {
      form.reset({
        title: inspirations.title,
        subtitle: inspirations.subtitle ?? "",
        sortOrder: 0,
        isActive: inspirations.isActive,
        validFrom: "",
        validUntil: "",
        treatmentIds: inspirations.treatments.map((t) => t.id),
      });
    } else if (collection) {
      form.reset({
        title: collection.title,
        subtitle: collection.subtitle ?? "",
        sortOrder: collection.sortOrder,
        isActive: collection.isActive,
        validFrom: collection.validFrom
          ? new Date(collection.validFrom).toISOString().slice(0, 10)
          : "",
        validUntil: collection.validUntil
          ? new Date(collection.validUntil).toISOString().slice(0, 10)
          : "",
        treatmentIds: collection.treatments.map((t) => t.id),
      });
    } else {
      form.reset({
        title: "",
        subtitle: "",
        sortOrder: 0,
        isActive: true,
        validFrom: "",
        validUntil: "",
        treatmentIds: [],
      });
    }
  }, [open, collection, inspirations, form]);

  const watchTreatmentIds = form.watch("treatmentIds");
  const watchActive = form.watch("isActive");

  const onSubmit = form.handleSubmit((data) => {
    if (isInspirations) {
      updateInspirations.mutate({
        title: data.title,
        subtitle: data.subtitle || null,
        isActive: data.isActive,
        treatmentIds: data.treatmentIds,
      });
      return;
    }
    if (data.treatmentIds.length === 0) {
      form.setError("treatmentIds", {
        message: "Wybierz przynajmniej jeden zabieg",
      });
      return;
    }

    const validFrom = data.validFrom
      ? new Date(data.validFrom + "T00:00:00Z").toISOString()
      : null;
    const validUntil = data.validUntil
      ? new Date(data.validUntil + "T23:59:59Z").toISOString()
      : null;

    const payload = {
      title: data.title,
      subtitle: data.subtitle || null,
      sortOrder: data.sortOrder,
      isActive: data.isActive,
      validFrom,
      validUntil,
      treatmentIds: data.treatmentIds,
    };

    if (isEdit && collection) {
      updateMutation.mutate({ id: collection.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  });

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    updateInspirations.isPending;

  const addTreatment = (id: string) => {
    const current = form.getValues("treatmentIds");
    if (!current.includes(id)) {
      form.setValue("treatmentIds", [...current, id], { shouldValidate: true });
    }
  };

  const removeTreatment = (id: string) => {
    const current = form.getValues("treatmentIds");
    form.setValue(
      "treatmentIds",
      current.filter((t) => t !== id),
      { shouldValidate: true },
    );
  };

  const moveTreatment = (id: string, direction: -1 | 1) => {
    const current = [...form.getValues("treatmentIds")];
    const idx = current.indexOf(id);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= current.length) return;
    [current[idx], current[target]] = [current[target]!, current[idx]!];
    form.setValue("treatmentIds", current, { shouldValidate: true });
  };

  const treatmentMap = new Map(
    (treatmentList?.items ?? []).map((t) => [t.id, t]),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isInspirations
              ? "Edytuj Inspiracje"
              : isEdit
                ? "Edytuj kolekcję"
                : "Nowa kolekcja"}
          </DialogTitle>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="col-title">
                {isInspirations ? "Tytuł sekcji" : "Tytuł belki"}
              </Label>
              <Input
                id="col-title"
                placeholder={
                  isInspirations ? "np. Inspiracje" : "np. Promocje tygodnia"
                }
                {...form.register("title")}
              />
              {form.formState.errors.title && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.title.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="col-subtitle">Podtytuł (opcjonalny)</Label>
              <Textarea
                id="col-subtitle"
                rows={2}
                {...form.register("subtitle")}
              />
            </div>

            {!isInspirations && (
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2 col-span-1">
                  <Label htmlFor="col-sort">Kolejność</Label>
                  <Input
                    id="col-sort"
                    type="number"
                    {...form.register("sortOrder")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Mniejsza = wyżej na liście
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="col-from">Ważna od</Label>
                  <Input
                    id="col-from"
                    type="date"
                    {...form.register("validFrom")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="col-until">Ważna do</Label>
                  <Input
                    id="col-until"
                    type="date"
                    {...form.register("validUntil")}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Switch
                id="col-active"
                checked={watchActive}
                onCheckedChange={(v) => form.setValue("isActive", v)}
              />
              <Label htmlFor="col-active">
                {isInspirations
                  ? watchActive
                    ? "Widoczna w aplikacji"
                    : "Ukryta w aplikacji"
                  : watchActive
                    ? "Aktywna"
                    : "Nieaktywna"}
              </Label>
            </div>

            <div className="space-y-2">
              <Label>
                {isInspirations ? "Zabiegi w sekcji" : "Zabiegi w belce"} (
                {watchTreatmentIds.length}) — kolejność = jak na mobile
              </Label>
              {isInspirations && (
                <p className="text-xs text-muted-foreground">
                  Pusta lista = wszystkie aktywne zabiegi salonu. Zabiegi z
                  aktywną ofertą, a potem z wyróżnieniem, zawsze trafiają na
                  początek; w każdej z tych grup obowiązuje kolejność ustawiona
                  tutaj.
                </p>
              )}
              <div className="rounded-md border divide-y">
                {watchTreatmentIds.length === 0 ? (
                  <p className="px-3 py-4 text-sm text-muted-foreground text-center">
                    {isInspirations
                      ? "Brak wybranych zabiegów — aplikacja pokaże wszystkie"
                      : "Brak wybranych zabiegów"}
                  </p>
                ) : (
                  watchTreatmentIds.map((id, idx) => {
                    const t = treatmentMap.get(id);
                    return (
                      <div
                        key={id}
                        className="flex items-center gap-2 px-3 py-2"
                      >
                        <span className="text-xs text-muted-foreground w-6 text-center">
                          {idx + 1}
                        </span>
                        <span className="flex-1 text-sm">
                          {t?.name ?? "(usunięty zabieg)"}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={idx === 0}
                          onClick={() => moveTreatment(id, -1)}
                        >
                          <ChevronUp className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={idx === watchTreatmentIds.length - 1}
                          onClick={() => moveTreatment(id, 1)}
                        >
                          <ChevronDown className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTreatment(id)}
                        >
                          <X className="size-4 text-destructive" />
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
              {form.formState.errors.treatmentIds && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.treatmentIds.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="col-add">Dodaj zabieg</Label>
              <TreatmentPicker
                id="col-add"
                mode="single"
                selectedIds={[]}
                excludeIds={watchTreatmentIds}
                onPick={addTreatment}
                enabled={open}
                listClassName="max-h-48"
                emptyText={
                  isInspirations
                    ? "Wszystkie zabiegi są już w sekcji"
                    : "Wszystkie zabiegi są już w belce"
                }
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Anuluj
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? "Zapisywanie..."
                : isEdit || isInspirations
                  ? "Zapisz zmiany"
                  : "Dodaj kolekcję"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
